import { motion } from 'framer-motion';
import styled, { css } from 'styled-components';

import { ReactComponent as StarSvgOutline } from '@pkg/assets/icons/v2/star-outline.svg';
import { ReactComponent as StarSvg } from '@pkg/assets/icons/v2/star.svg';
import { styledTypography } from '@pkg/primitives';

interface Props {
    $isAdded?: boolean;
    $loading?: boolean;
    $isPrimary?: boolean;
    $isPrimaryAdded?: boolean;
}

export const Button = styled(motion.button)<Props>`
    border-radius: 40px;

    font-weight: 700;
    text-align: center;
    letter-spacing: -0.02em;
    color: #ffffff;

    outline: none;
    border: none;

    cursor: pointer;

    padding: 0 20px;

    white-space: nowrap;
    border: 1px solid transparent;

    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;

    height: 48px;
    min-height: 44px;
    font-size: 14px;
    line-height: 14px;
    background: rgba(255, 255, 255, 0);
    border: 1px solid #ffffff;

    svg path {
        fill: transparent;
        stroke: white;
        stroke-width: 2px;
    }

    ${({ $isPrimary }) =>
        !$isPrimary &&
        css`
            &:hover {
                background: rgba(255, 255, 255, 0.05);
                svg path {
                    fill: white;
                }
            }
        `}

    ${({ $isAdded, $isPrimary }) =>
        $isAdded &&
        !$isPrimary &&
        css`
            background: radial-gradient(
                    67.31% 175% at 50% 148.75%,
                    rgba(255, 255, 255, 0.001) 0%,
                    rgba(0, 0, 0, 0) 100%
                ),
                rgba(255, 255, 255, 0.1);
            svg path {
                fill: white;
            }
            &:hover {
                background: radial-gradient(
                        67.31% 175% at 50% 148.75%,
                        rgba(255, 255, 255, 0.001) 0%,
                        rgba(0, 0, 0, 0) 100%
                    ),
                    rgba(255, 255, 255, 0.12);
                svg path {
                    fill: transparent;
                }
            }
        `}

    ${({ $isPrimary }) =>
        $isPrimary &&
        css`
            width: 100%;
            border: 0 solid transparent;
            background: linear-gradient(300.86deg, #523df1 39.32%, #976af6 92.76%);
        `}

    ${({ $isPrimary, $isAdded }) =>
        $isPrimary &&
        $isAdded &&
        css`
            svg path {
                fill: white;
            }
        `}


    ${({ $loading }) =>
        $loading &&
        css`
            pointer-events: none;
            color: transparent;
            opacity: 0.5;
            border: 1px solid transparent;
            background: radial-gradient(
                    67.31% 175% at 50% 148.75%,
                    rgba(255, 255, 255, 0.001) 0%,
                    rgba(0, 0, 0, 0) 100%
                ),
                rgba(255, 255, 255, 0.12);
            svg path {
                fill: transparent;
                stroke: transparent;
            }
        `};

    @media (min-width: ${({ theme }) => theme.breakpoints.mobile}) {
        height: 56px;
        padding: 0 30px;
        font-size: 16px;
        line-height: 16px;
    }
`;

/* Icon Button
-------------------------------  */

export const IconButtonWrapper = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`;

export const Number = styled.div`
    font-weight: 400;
    font-size: 12px;
    line-height: 14px;
    color: rgba(255, 255, 255, 0.5);
`;

export const StarIconStyled = styled(StarSvg)`
    width: 16px;
    height: 16px;

    display: none;

    transform-origin: center center;
`;

export const StarIconOutlineStyled = styled(StarSvgOutline)`
    width: 16px;
    height: 16px;

    fill: none;

    transform-origin: center center;
`;

export const StarButton = styled.button<{ $isLoading?: boolean; $inWishlist?: boolean }>`
    width: 24px;
    height: 24px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    border: none;
    outline: none;
    padding: 0;
    position: relative;

    border-radius: 7px;

    svg {
        width: 16px;
        height: 16px;
        transform-origin: center center;
        fill: none;
    }

    &:hover {
        box-shadow:
            0 0 68px 0 rgba(255, 255, 255, 0.1) inset,
            0 0 0 1px rgba(255, 255, 255, 0.1) inset;

        svg {
            scale: 1.2;
        }
    }

    &:active {
        box-shadow:
            0 0 68px 0 rgba(255, 255, 255, 0.1) inset,
            0 0 0 1px rgba(255, 255, 255, 0.1) inset;

        svg {
            scale: 1;
        }
    }

    background: rgba(255, 255, 255, 0.05);

    ${({ $inWishlist }) =>
        $inWishlist &&
        css`
            background: #523df1;
            svg {
                fill: white;

                path {
                    stroke-width: 0;
                }
            }
        `}

    ${({ $isLoading }) =>
        $isLoading &&
        css`
            background: rgba(255, 255, 255, 0.05);
            pointer-events: none;
        `}
`;

export const ACTION_TEXT_STYLE = css`
    ${styledTypography.allianceBase};
    display: flex;
    font-weight: 700;
    font-size: 10px;
    color: ${({ theme }) => theme.colors.white};
`;

export const CountText = styled.p`
    ${ACTION_TEXT_STYLE};
    text-transform: uppercase;
`;
